import type { IsoDate, IsoDateTime } from './common';
import type { OrderId, ProductId, WarehouseId } from './ids';
import type { MarketplaceChannel } from './marketplace';

/** A product ordered in this many days counts as in demand on the "Stoğu olmayan ürünler" list. */
export const STOCK_WAITING_RECENT_DAYS = 30;
/** The Telegram digest names orders waiting at least this long (a new order is reserved within seconds). */
export const STOCK_WAITING_DIGEST_MIN_MINUTES = 60;
/** At most this many orders in the digest, the longest waiting first; the rest are counted. */
export const STOCK_WAITING_DIGEST_LINES = 15;

/** An order waiting for stock (backordered), under a product it needs on the Stok bekleyen list. */
export interface StockWaitingOrder {
  readonly orderId: OrderId;
  readonly orderNumber: string;
  readonly channel: MarketplaceChannel;
  /** units of this product the order needs */
  readonly qty: number;
  /** since when it waits: it turned backordered then (an order of before that was stamped: its creation) */
  readonly waitingSince: IsoDateTime;
  readonly orderedAt: IsoDateTime;
  /** son kargo zamanı, when known */
  readonly shipBy: IsoDateTime | null;
}

/** An open shipment to the warehouse (ASN, beklenen or kabulde) that brings the product. */
export interface StockWaitingInbound {
  readonly shipmentId: string;
  readonly asnNumber: string;
  readonly status: 'expected' | 'receiving';
  readonly expectedAt: IsoDate | null;
  /** units still to come: announced minus counted (sağlam and hasarlı) */
  readonly remaining: number;
}

/** What a connected marketplace account showed in stock for the product (its own count, single-unit listings). */
export interface MarketplaceStockSeen {
  readonly qty: number;
  /** when that account's product list was read */
  readonly seenAt: IsoDateTime;
}

/** A product orders wait for, with the orders, the stock the warehouse has of it and what may bring more. */
export interface StockWaitingProduct {
  readonly productId: ProductId;
  readonly sku: string;
  readonly name: string;
  readonly imageUrl: string | null;
  readonly tenantCode: string;
  /** units its waiting orders need, and how many of them the free stock does not cover */
  readonly needed: number;
  readonly missing: number;
  /** in this warehouse: every unit there, the ones free to reserve, the ones received and not put away yet */
  readonly onHand: number;
  readonly available: number;
  readonly atDock: number;
  /** units of a cancelled order or a customer return waiting to go back to a shelf (Yerleştirme → Rafa iade) */
  readonly toShelf: number;
  /** the longest waiting first */
  readonly orders: readonly StockWaitingOrder[];
  readonly inbound: readonly StockWaitingInbound[];
  readonly marketplace: MarketplaceStockSeen | null;
  /** the wait of its longest waiting order */
  readonly waitingSince: IsoDateTime;
}

export const OUT_OF_STOCK_URGENCIES = ['high', 'medium', 'low'] as const;
/** high: in demand and nothing on the way; medium: in demand, a shipment brings it; low: only a shipment brings it. */
export type OutOfStockUrgency = (typeof OUT_OF_STOCK_URGENCIES)[number];

export const OUT_OF_STOCK_URGENCY_LABELS: Readonly<Record<OutOfStockUrgency, string>> = {
  high: 'Satışta, gönderi yok',
  medium: 'Satışta, gönderi yolda',
  low: 'Gönderi yolda',
};

/** A product without a unit in the warehouse that sells, is on the way, or shows stock on a marketplace. */
export interface OutOfStockProduct {
  readonly productId: ProductId;
  readonly sku: string;
  readonly name: string;
  readonly imageUrl: string | null;
  readonly tenantCode: string;
  /** orders of the warehouse that wanted it in the last STOCK_WAITING_RECENT_DAYS days */
  readonly recentOrders: number;
  readonly lastOrderedAt: IsoDateTime | null;
  readonly inbound: readonly StockWaitingInbound[];
  readonly marketplace: MarketplaceStockSeen | null;
  readonly urgency: OutOfStockUrgency;
}

/** GET /api/wms/stock-waiting: Stok bekleyen siparişler of a warehouse, by product, and its products with no stock. */
export interface StockWaitingList {
  readonly warehouseId: WarehouseId;
  /** orders waiting for stock in the warehouse */
  readonly orders: number;
  /** the products they wait for, the longest waiting first */
  readonly products: readonly StockWaitingProduct[];
  /** Stoğu olmayan ürünler (not those above), the most urgent first */
  readonly outOfStock: readonly OutOfStockProduct[];
}

/** What the Özet banner says about orders waiting for stock (FloorSummary.stockWaiting). */
export interface StockWaitingSummary {
  readonly orders: number;
  /** the wait of the longest waiting one */
  readonly oldestSince: IsoDateTime | null;
  /** how many products they wait for, and the SKUs of a few of them, the longest waiting first */
  readonly products: number;
  readonly skus: readonly string[];
}

/** "Yeniden dene" on a product: the order numbers reserved now, and those still waiting. */
export interface StockWaitingRetry {
  readonly allocated: readonly string[];
  readonly waiting: readonly string[];
}

/** How urgent a product without stock is (the list's order: high, medium, low). */
export function outOfStockUrgency(p: { readonly recentOrders: number; readonly marketplaceQty: number; readonly inboundRemaining: number }): OutOfStockUrgency {
  const wanted = p.recentOrders > 0 || p.marketplaceQty > 0;
  if (!wanted) return 'low';
  return p.inboundRemaining > 0 ? 'medium' : 'high';
}

/** The order of the "Stoğu olmayan ürünler" list: by urgency, then the most ordered, then the most seen on a marketplace. */
export function compareOutOfStock(a: OutOfStockProduct, b: OutOfStockProduct): number {
  const rank = (p: OutOfStockProduct) => OUT_OF_STOCK_URGENCIES.indexOf(p.urgency);
  return (
    rank(a) - rank(b) ||
    b.recentOrders - a.recentOrders ||
    (b.marketplace?.qty ?? 0) - (a.marketplace?.qty ?? 0) ||
    a.sku.localeCompare(b.sku, 'tr') ||
    a.tenantCode.localeCompare(b.tenantCode, 'tr')
  );
}

/** "45 dakikadır", "5 saattir", "2 gündür": how long something has waited, as the floor says it. */
export function waitingFor(since: IsoDateTime | Date, now: Date): string {
  const minutes = Math.max(0, Math.floor((now.getTime() - new Date(since).getTime()) / 60_000));
  if (minutes < 60) return `${Math.max(1, minutes)} dakikadır`;
  if (minutes < 24 * 60) return `${Math.floor(minutes / 60)} saattir`;
  return `${Math.floor(minutes / (24 * 60))} gündür`;
}

/** An order of the Telegram digest: numbers and codes only, never the buyer. */
export interface StockWaitingDigestOrder {
  readonly orderNumber: string;
  readonly tenantCode: string;
  readonly warehouseCode: string;
  /** what it waits for: SKU and units */
  readonly products: readonly { readonly sku: string; readonly qty: number }[];
  readonly waitingSince: IsoDateTime;
}

/**
 * The digest of orders waiting for stock sent to the warehouse's Telegram group (or webhook) twice a
 * day; null when none waits. The longest waiting first, at most `maxLines` of them, then how many more.
 * The warehouse is named only when the orders are in more than one.
 */
export function stockWaitingDigest(orders: readonly StockWaitingDigestOrder[], now: Date, maxLines: number = STOCK_WAITING_DIGEST_LINES): string | null {
  if (orders.length === 0) return null;
  const sorted = [...orders].sort((a, b) => a.waitingSince.localeCompare(b.waitingSince) || a.orderNumber.localeCompare(b.orderNumber));
  const warehouses = new Set(sorted.map((o) => o.warehouseCode)).size > 1;
  const lines = sorted.slice(0, maxLines).map((o) => {
    const goods = o.products.map((p) => `${p.sku} ×${p.qty}`).join(', ');
    return `📦 ${warehouses ? `${o.warehouseCode} · ` : ''}${o.orderNumber} — ${goods} (${o.tenantCode}), ${waitingFor(o.waitingSince, now)}`;
  });
  const more = sorted.length - lines.length;
  return ['Pronto Depo · Stok bekleyen siparişler', ...lines, ...(more > 0 ? [`… ve ${more} sipariş daha`] : [])].join('\n');
}
